import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { fetchRenessansDialogResults, fetchRenessansOperators, fetchRenessansSummary } from './api';
import { RenessansDialogResultsParams, RenessansOperatorsParams, RenessansSummaryParams } from './types';

/**
 * Ключи кэша TanStack Query для дашборда Ренессанс.
 * Параметры включены в ключ: таблица операторов и диаграмма сравнения ходят за одними
 * и теми же данными с разными параметрами и кэшируются раздельно.
 */
export const renessansDashboardKeys = {
  all: [API_TAGS.RENESSANS_DASHBOARDS] as const,
  summary: (params?: RenessansSummaryParams) => [...renessansDashboardKeys.all, 'summary', { ...params }] as const,
  operators: (params?: RenessansOperatorsParams) =>
    [...renessansDashboardKeys.all, 'operators', { ...params }] as const,
  dialogResults: (params?: RenessansDialogResultsParams) =>
    [...renessansDashboardKeys.all, 'dialogResults', { ...params }] as const,
};

/**
 * Хук KPI и временных рядов периода.
 * `keepPreviousData` — без мигания при смене фильтров.
 */
export const useRenessansSummary = (params?: RenessansSummaryParams) =>
  useQuery({
    queryKey: renessansDashboardKeys.summary(params),
    queryFn: () => fetchRenessansSummary(params),
    placeholderData: keepPreviousData,
  });

/**
 * Хук статистики по операторам или по группам операторов.
 * Таблица и диаграмма сравнения делают независимые вызовы — кэшируются раздельно.
 */
export const useRenessansOperators = (params?: RenessansOperatorsParams) =>
  useQuery({
    queryKey: renessansDashboardKeys.operators(params),
    queryFn: () => fetchRenessansOperators(params),
    placeholderData: keepPreviousData,
  });

/**
 * Хук распределения диалогов по результату.
 */
export const useRenessansDialogResults = (params?: RenessansDialogResultsParams) =>
  useQuery({
    queryKey: renessansDashboardKeys.dialogResults(params),
    queryFn: () => fetchRenessansDialogResults(params),
    placeholderData: keepPreviousData,
  });
